import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import LoadingBackdrop from "../../../components/LoadingBackdrop.js";
import { useUser } from "../../../components/UserContext.js";
import DataBox from "../../../components/data-box/DataBox.js";
import ErrorDialog from "../../../components/dialogs/ErrorDialog.js";
import LogsList from "../../../components/page-item/LogsList.js";
import PageListHeader from "../../../components/page-list/PageListHeader.js";
import { getCharacter, getHost, getLog } from "../../../lib/apiConfig.js";
import { PAGE_CONTAINER_SX, PAGE_TITLE_SX } from "../../../lib/constants.js";
import { formatDate } from "../../../lib/formatDate.js";
import { formatSecurityLog } from "../../../lib/formatSecurityLog.js";
import { useFetch } from "../../../lib/hooks/useFetch.js";

export default function HostLogPage({ locales }) {
  const { isHost, runId } = useUser();
  const { state } = useLocation();
  const [hostId, setHostId] = useState(null);
  const [host, setHost] = useState(null);
  const [characterId, setCharacterId] = useState(null);
  const [character, setCharacter] = useState(null);
  const [logs, setLogs] = useState(null);
  const [formattedLogs, setFormattedLogs] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isAscending, setIsAscending] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const setError = (error) => {
    setErrorMessage(error.message);
    setShowError(true);
  };

  const { isFetching: isFetchingHost } = useFetch({
    endpoint: getHost({ id: hostId, runId }),
    mustFetch: hostId && !host,
    onSuccess: (response) => {
      setHost(response);
      setCharacterId(response?.character?.id);
    },
    onFail: setError,
  });

  const { isFetching: isFetchingCharacter } = useFetch({
    endpoint: getCharacter({ id: characterId, runId }),
    mustFetch: characterId && !character,
    onSuccess: setCharacter,
    onFail: setError,
  });

  const { isFetching: isFetchingLog } = useFetch({
    endpoint: getLog({ filter: `hostId=${hostId}`, runId }),
    mustFetch: hostId && !logs,
    onSuccess: (response) => {
      setLogs(response);
      setFormattedLogs(
        response.map((log) => ({
          ...log,
          formattedLog: formatSecurityLog({ locales, log }),
        }))
      );
    },
    onFail: setError,
  });

  useEffect(() => {
    setHostId(state.id);
  }, [state]);

  const filteredMessages = useMemo(() => {
    const filtered = formattedLogs.filter((log) =>
      log.formattedLog.toLowerCase().includes(searchValue.toLowerCase())
    );

    return filtered.sort(
      (a, b) =>
        (new Date(a.createdAt) - new Date(b.createdAt)) * (isAscending ? 1 : -1)
    );
  }, [isAscending, formattedLogs, searchValue]);

  const handleInput = ({ target }) => {
    setSearchValue(target.value);
  };

  const handleClearInput = () => {
    setSearchValue("");
  };

  const handleSort = () => {
    setIsAscending(!isAscending);
  };

  return !host || isFetchingHost || isFetchingCharacter || isFetchingLog ? (
    <LoadingBackdrop />
  ) : (
    <Container disableGutters sx={PAGE_CONTAINER_SX}>
      {isHost ? null : (
        <Typography id="host-log-header" component="h1" sx={PAGE_TITLE_SX}>
          {host.serial}
        </Typography>
      )}
      <Grid container id="host-log-page">
        {isHost ? null : (
          <>
            <Grid xs={12} smd={4} md={3}>
              <DataBox locales={locales} character={character} host={host} />
            </Grid>
            <Grid xs={12} smd={1} />
          </>
        )}
        <Grid
          xs={12}
          smd={isHost ? 12 : 7}
          md={isHost ? 12 : 8}
          sx={{ mt: { xs: 4, smd: 0 } }}
        >
          <PageListHeader
            handleClearInput={handleClearInput}
            handleInput={handleInput}
            handleSort={handleSort}
            locales={locales}
            isAscending={isAscending}
            pageName="host-log"
            searchBoxLabel={locales["logs.search"]}
            searchValue={searchValue}
          />
          {filteredMessages.length ? (
            <LogsList
              id="host-log"
              items={filteredMessages}
              renderPrimary={(log) => log.formattedLog}
              renderSecondary={(log) => formatDate(log.createdAt)}
            />
          ) : (
            <Typography component="p">{locales["search.empty"]}</Typography>
          )}
        </Grid>
      </Grid>
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </Container>
  );
}
